// src/Pages/LoginPage.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, forgotPassword } from "../redux/actions/authActions";
import { Link, useNavigate } from "react-router-dom";
import { dealerAccess } from "../redux/actions/orderActions"; 

// import { fetchDashboardData } from "../redux/actions/dashboardActions"; // Removed - using static menu data
import { FiEye, FiEyeOff } from "react-icons/fi";

const Login = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    mobile: "",
    password: "",
  });

  const { loading, userInfo, error } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);

  const [rememberMe, setRememberMe] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1 = mobile, 2 = otp+password
  const [forgotMobile, setForgotMobile] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");

  // const handleChange = (e) => {
  //   setFormData((prev) => ({
  //     ...prev,
  //     [e.target.name]: e.target.value,
  //   }));
  // };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobile") {
      // Allow only digits, no more than 10, and starting with 6-9
      const cleanedValue = value.replace(/\D/g, ""); // remove non-digit characters

      // If input is empty or starts with 6-9 and <= 10 digits
      if (
        cleanedValue === "" ||
        (/^[6-9]/.test(cleanedValue) && cleanedValue.length <= 10)
      ) {
        setFormData((prev) => ({
          ...prev,
          [name]: cleanedValue,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  useEffect(() => {
    const savedLogin = localStorage.getItem("rememberedLogin");
    if (savedLogin) {
      const parsed = JSON.parse(savedLogin);
      setFormData(parsed);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(loginUser(formData)).then(() => {
      const user = JSON.parse(localStorage.getItem("userInfo"));
      const token = user?.access_token;

      // Decode JWT payload to get user_type (no need to depend on backend storing it again)
      const base64Url = token?.split(".")[1];
      const base64 = base64Url?.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = base64 ? JSON.parse(atob(base64)) : null;

      const userType = jsonPayload?.user_type;
      if (rememberMe) {
        localStorage.setItem("rememberedLogin", JSON.stringify(formData));
      } else {
        localStorage.removeItem("rememberedLogin");
      }

      if (token) {
        // Removed fetchDashboardData call - menu data is now static
        // dispatch(fetchDashboardData(token));

        if (userType === "Super Admin") {
          navigate("/admin-dashboard");
        } else {
          navigate("/dashboard");
        }
      }
    });
  };

  // Forgot password: mobile input change with validation
  const handleForgotMobileChange = (e) => {
    const value = e.target.value;
    const cleanedValue = value.replace(/\D/g, "");

    if (
      cleanedValue === "" ||
      (/^[6-9]/.test(cleanedValue) && cleanedValue.length <= 10)
    ) {
      setForgotMobile(cleanedValue);
    }
  };

  const handleForgotGenerateOtp = async (e) => {
    e.preventDefault();
    if (forgotMobile.length !== 10) {
      return;
    }

    const result = await dispatch(forgotPassword({ mobile: forgotMobile }));
    if (result.success) {
      setForgotStep(2);
      setForgotOtp("");
      setForgotNewPassword("");
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (forgotMobile.length !== 10 || forgotOtp.length !== 4 || !forgotNewPassword) {
      return;
    }

    const payload = {
      mobile: forgotMobile,
      otp: forgotOtp,
      new_password: forgotNewPassword,
    };

    const result = await dispatch(forgotPassword(payload));
    if (result.success) {
      // Reset forgot state and hide modal
      setShowForgot(false);
      setForgotStep(1);
      setForgotMobile("");
      setForgotOtp("");
      setForgotNewPassword("");
    }
  };

  return (
    <div className="relative flex flex-col justify-center items-center min-h-screen bg-gray-100 overflow-hidden">
      {/* Enhanced Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-amber-100 to-amber-50"></div>
        
        {/* Animated floating circles with enhanced effects */}
        <div className="absolute top-20 left-20 w-32 h-32 bg-amber-300/30 rounded-full blur-3xl animate-bounce" style={{ animationDuration: '6s' }}></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-amber-400/30 rounded-full blur-3xl animate-bounce" style={{ animationDuration: '8s', animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-amber-500/30 rounded-full blur-2xl animate-pulse" style={{ animationDuration: '4s' }}></div>
        <div className="absolute bottom-1/4 right-1/3 w-36 h-36 bg-amber-600/30 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s', animationDelay: '2s' }}></div>
        <div className="absolute top-1/3 right-1/4 w-28 h-28 bg-amber-400/25 rounded-full blur-2xl animate-bounce" style={{ animationDuration: '7s', animationDelay: '0.5s' }}></div>
        <div className="absolute bottom-1/3 left-1/3 w-44 h-44 bg-amber-500/25 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s', animationDelay: '1.5s' }}></div>
        
        {/* Animated gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-200/10 to-transparent animate-pulse" style={{ animationDuration: '3s' }}></div>
      </div>

      {/* Project Name - Attractive Header */}
      <div className="relative z-10 mb-8 text-center px-4">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-2 tracking-tight">
          <span 
            className="inline-block leading-relaxed bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 bg-clip-text text-transparent animate-gradient bg-[length:200%_auto]"
            style={{ 
              backgroundImage: 'linear-gradient(to right, #D97706, #F59E0B, #D97706)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundSize: '200% auto'
            }}
          >
            Easy Rewardz
          </span>
{/*           <br /> */}
{/*           <span  */}
{/*             className="inline-block leading-relaxed bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 bg-clip-text text-transparent animate-gradient bg-[length:200%_auto]" */}
{/*             style={{  */}
{/*               backgroundImage: 'linear-gradient(to right, #F59E0B, #D97706, #F59E0B)', */}
{/*               WebkitBackgroundClip: 'text', */}
{/*               WebkitTextFillColor: 'transparent', */}
{/*               backgroundSize: '200% auto', */}
{/*               animationDelay: '0.5s', */}
{/*             }} */}
{/*           > */}
{/*             Rewardz */}
{/*           </span> */}
        </h1>
        <div className="w-32 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto mt-2 rounded-full animate-pulse"></div>
      </div>

      {/* Login Form */}
      <form
        onSubmit={handleSubmit}
        className="relative z-10 bg-white border-2 border-amber-500 rounded-xl p-8 shadow-2xl w-full max-w-md backdrop-blur-sm bg-white/98"
      >
        <h2 className="text-2xl font-bold text-center mb-6 text-amber-600">
          Login
        </h2>

        <div className="mb-4 relative">
          <input
            type="text"
            name="mobile"
            value={formData.mobile}
            onChange={handleChange}
            className=" peer w-full border border-gray-300 rounded-md px-3 pt-5 pb-2 text-sm focus:outline-none focus:border-blue-500"
            placeholder=" "
            required
          />
          <label
            htmlFor="mobile"
            className="pointer-events-none absolute left-2.5 -top-2 text-xs bg-white px-1 transition-all duration-200 text-gray-500
                  peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400
                  peer-focus:-top-2 peer-focus:text-xs peer-focus:text-blue-500"
          >
            Mobile
          </label>
        </div>

        <div className="mb-6 relative">
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            value={formData.password}
            onChange={handleChange}
            className="peer w-full border border-gray-300 rounded-md px-3 pt-5 pb-2 text-sm focus:outline-none focus:border-blue-500 pr-10"
            placeholder=" "
            required
          />
          <label
            htmlFor="password"
            className="pointer-events-none absolute left-2.5 -top-2 text-xs bg-white px-1 transition-all duration-200 text-gray-500
      peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400
      peer-focus:-top-2 peer-focus:text-xs peer-focus:text-blue-500"
          >
            Password
          </label>

          {/* Eye icon for toggling visibility */}
          <div
            className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <FiEyeOff size={20} className="text-amber-600" />
            ) : (
              <FiEye size={20} className="text-amber-600" />
            )}
          </div>
        </div>
        <div className="mt-2 mb-4 flex items-center">
          <input
            type="checkbox"
            id="rememberMe"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="mr-2 text-amber-600"
          />
          <label htmlFor="rememberMe" className="text-sm text-gray-900">
            Remember Me
          </label>
        </div>
        <button
          type="submit"
          className="flex flex-col justify-center items-center w-1/3 mx-auto py-2 bg-amber-500 text-gray-950 rounded-lg hover:bg-amber-600 transition"
          disabled={loading}
        >
          {loading ? "Logging in..." : "Login"}
        </button>

        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => {
              setShowForgot(true);
              setForgotStep(1);
              setForgotMobile("");
              setForgotOtp("");
              setForgotNewPassword("");
            }}
            className="text-xs text-amber-700 hover:text-amber-800 hover:underline"
          >
            Forgot password?
          </button>
        </div>

        {/* <p className="text-sm text-center mt-4">
          Don't have an account?{" "}
          <Link to="/" className="text-blue-500 hover:underline">
            Register
          </Link>
        </p> */}
      </form>

      {/* Forgot Password Modal */}
      {showForgot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-xl shadow-2xl border border-amber-200 w-full max-w-md p-6 relative">
            <h3 className="text-lg font-bold text-amber-600 mb-4 text-center">
              Forgot Password
            </h3>

            {forgotStep === 1 ? (
              <form onSubmit={handleForgotGenerateOtp}>
                <div className="mb-4 relative">
                  <input
                    type="text"
                    name="forgotMobile"
                    value={forgotMobile}
                    onChange={handleForgotMobileChange}
                    className="peer w-full border border-gray-300 rounded-md px-3 pt-5 pb-2 text-sm focus:outline-none focus:border-amber-500"
                    placeholder=" "
                    required
                  />
                  <label className="pointer-events-none absolute left-2.5 -top-2 text-xs bg-white px-1 transition-all duration-200 text-gray-500
                    peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400
                    peer-focus:-top-2 peer-focus:text-xs peer-focus:text-amber-500">
                    Registered Mobile Number
                  </label>
                </div>
                <button
                  type="submit"
                  className="w-full py-2.5 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                  disabled={forgotMobile.length !== 10 || loading}
                >
                  Send OTP
                </button>
              </form>
            ) : (
              <form onSubmit={handleForgotSubmit} className="space-y-4">
                <p className="text-xs text-gray-600 text-center">
                  OTP has been sent to{" "}
                  <span className="font-semibold">{forgotMobile}</span>
                </p>

                <div className="mb-2 grid grid-cols-1 gap-3">
                  <div className="relative">
                    <input
                      type="text"
                      value={forgotOtp}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, "");
                        if (v.length <= 4) setForgotOtp(v);
                      }}
                      className="peer w-full border border-gray-300 rounded-md px-3 pt-5 pb-2 text-sm focus:outline-none focus:border-amber-500"
                      placeholder=" "
                      maxLength={4}
                      required
                    />
                    <label className="pointer-events-none absolute left-2.5 -top-2 text-xs bg-white px-1 transition-all duration-200 text-gray-500
                      peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400
                      peer-focus:-top-2 peer-focus:text-xs peer-focus:text-amber-500">
                      OTP
                    </label>
                  </div>

                  <div className="relative">
                    <input
                      type="password"
                      value={forgotNewPassword}
                      onChange={(e) => setForgotNewPassword(e.target.value)}
                      className="peer w-full border border-gray-300 rounded-md px-3 pt-5 pb-2 text-sm focus:outline-none focus:border-amber-500"
                      placeholder=" "
                      required
                    />
                    <label className="pointer-events-none absolute left-2.5 -top-2 text-xs bg-white px-1 transition-all duration-200 text-gray-500
                      peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400
                      peer-focus:-top-2 peer-focus:text-xs peer-focus:text-amber-500">
                      New Password
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full py-2.5 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                  disabled={
                    loading ||
                    forgotMobile.length !== 10 ||
                    forgotOtp.length !== 4 ||
                    !forgotNewPassword
                  }
                >
                  Reset Password
                </button>
              </form>
            )}

            <button
              type="button"
              onClick={() => {
                setShowForgot(false);
                setForgotStep(1);
              }}
              className="absolute top-3 right-4 text-gray-500 hover:text-gray-800 text-sm"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
